import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { Heading, Text } from '@wix/design-system';
import { useAuthTokenContext } from '../../context/AuthTokenContext';
import { TokenAuthenticatedHttpClientFactory } from '../../utils/TokenAuthenticatedHttpClientFactory';
import s from './pocChat.scss';
import { AssistantMessage, usePocChat } from './usePocChat';

const clientFactory = new TokenAuthenticatedHttpClientFactory();

function formatTime(timestamp: string): string {
  try {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export const PocChat: FC = () => {
  const { authToken } = useAuthTokenContext();
  const signedInstance = window.__APP_SIGNED_INSTANCE__ || '';
  const httpClient = useMemo(() => clientFactory.createHttpClient(authToken, { baseURL: '/poc-assistant' }), [authToken]);

  const { messages, isLoading, error, lastTaskCreated, sendMessage, clearConversation } = usePocChat(httpClient, signedInstance);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = () => {
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={s.container}>
      <div className={s.header}>
        <div>
          <Heading size="medium" className={s.headerTitle}>
            Orion
          </Heading>
          <Text secondary size="small" className={s.headerSubtitle}>
            Scheduling Assistant
          </Text>
        </div>
        <button className={s.clearButton} onClick={clearConversation} title="Clear conversation">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className={s.messageList}>
        {messages.length === 0 && !isLoading && (
          <div className={s.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#271e38" strokeWidth="1.5" opacity="0.4">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <Text size="medium">What would you like to schedule?</Text>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={`${msg.timestamp}-${i}`} message={msg} />
        ))}

        {isLoading && (
          <div className={s.typingIndicator}>
            <div className={s.avatar}>
              <span className={s.avatarLetter}>O</span>
            </div>
            <div className={s.typingDots}>
              <div className={s.typingDot} />
              <div className={s.typingDot} />
              <div className={s.typingDot} />
            </div>
          </div>
        )}

        {lastTaskCreated && (
          <div className={s.taskCard}>
            <div className={s.taskIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </div>
            <div className={s.taskInfo}>
              <Text size="small" weight="bold">
                Task Created
              </Text>
              <br />
              <Text size="small">{lastTaskCreated.title}</Text>
              <br />
              <Text size="tiny" secondary>
                {lastTaskCreated.id}
              </Text>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className={s.errorBar}>
          <Text size="tiny" skin="error">
            {error}
          </Text>
        </div>
      )}

      <div className={s.inputArea}>
        <div className={s.inputWrapper}>
          <input
            className={s.input}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Schedule a meeting..."
            disabled={isLoading}
          />
          <button className={s.sendButton} onClick={handleSend} disabled={isLoading || !inputValue.trim()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>

      <div className={s.footer}>
        <Text size="tiny" secondary>
          Powered by Orion
        </Text>
      </div>
    </div>
  );
};

const MessageBubble: FC<{ message: AssistantMessage }> = ({ message }) => {
  if (message.role === 'thinking') {
    return (
      <div className={s.thinkingRow}>
        <div className={s.thinkingDot} />
        <Text size="tiny" className={s.thinkingText}>
          {message.content}
        </Text>
      </div>
    );
  }

  const isUser = message.role === 'user';

  return (
    <div>
      <div className={isUser ? s.messageRowUser : s.messageRow}>
        {!isUser && (
          <div className={s.avatar}>
            <span className={s.avatarLetter}>O</span>
          </div>
        )}
        <div className={isUser ? s.userBubble : s.assistantBubble}>
          {isUser ? (
            <Text size="small" light>
              {message.content}
            </Text>
          ) : (
            <Text size="small">
              <Markdown>{message.content}</Markdown>
            </Text>
          )}
          {message.timestamp && <div className={isUser ? s.timestampLight : s.timestamp}>{formatTime(message.timestamp)}</div>}
        </div>
      </div>
    </div>
  );
};
